<template>
  <div class="liquid-pressure-pro">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>p = ρgh$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 实验室 -->
      <div ref="canvasContainer" class="canvas-3d-wrapper">
         <div class="hud-top">
            <div class="monitor">
               <label>当前深度 h</label>
               <span>{{ (depth / 10).toFixed(2) }} m</span>
            </div>
            <div class="monitor">
               <label>理论压强 P</label>
               <span>{{ (rho * 9.8 * depth / 10).toFixed(0) }} Pa</span>
            </div>
         </div>
         
         <div class="gauge-overlay">
            <svg width="80" height="200" viewBox="0 0 40 100">
               <rect x="15" y="0" width="10" height="100" fill="rgba(255,255,255,0.05)" />
               <rect x="15" :y="100 - (depth/60*100)" width="10" :height="depth/60*100" fill="#00d2ff">
                  <animate attributeName="opacity" values="0.8;0.4;0.8" dur="2s" repeatCount="indefinite" />
               </rect>
            </svg>
            <div class="label">压力计</div>
         </div>
      </div>

      <!-- 控制面板 -->
      <div class="bottom-controls">
         <div class="control-grid glass">
            <div class="item">
               <label>探头深度 (h)</label>
               <el-slider v-model="depth" :min="0" :max="55" size="mini"></el-slider>
            </div>
            <div class="item">
               <label>液体密度 (ρ)</label>
               <el-slider v-model="rho" :min="800" :max="1500" :step="100" size="mini"></el-slider>
            </div>
         </div>

         <div class="result-card">
            <div class="icon-box"><i class="el-icon-odometer"></i></div>
            <p><strong>实验结论：</strong>液体内部压强随深度增加而线性增大。在同一深度，液体密度越大，压强越大。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'LiquidPressureViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      depth: 20,
      rho: 1000,
      running: true
    }
  },
  mounted() {
    this.initThree();
  },
  beforeDestroy() {
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  watch: {
    depth() { this.syncView(); },
    rho() { this.syncView(); }
  },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(40, 50, 80);
      this.camera.lookAt(0, 25, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Tank
      const tankGeo = new THREE.BoxGeometry(40, 60, 40);
      const tankMat = new THREE.MeshPhongMaterial({ color: 0x00d2ff, transparent: true, opacity: 0.1, side: THREE.BackSide, shininess: 100 });
      this.tank = new THREE.Mesh(tankGeo, tankMat);
      this.tank.position.y = 30;
      this.scene.add(this.tank);

      // Water Level
      this.water = new THREE.Mesh(new THREE.BoxGeometry(39.8, 55, 39.8), new THREE.MeshPhongMaterial({ color: 0x006efd, transparent: true, opacity: 0.3 }));
      this.water.position.y = 27.5;
      this.scene.add(this.water);

      // Probe (U-tube end)
      const probeGeo = new THREE.CylinderGeometry(4, 4, 2, 32);
      this.probe = new THREE.Mesh(probeGeo, new THREE.MeshPhongMaterial({ color: 0x333 }));
      this.scene.add(this.probe);

      // Pressure Visualization Arrows
      this.arrowGroup = new THREE.Group();
      this.scene.add(this.arrowGroup);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.4));
      const light = new THREE.DirectionalLight(0xffffff, 0.8);
      light.position.set(20, 80, 20);
      this.scene.add(light);

      this.syncView();
      this.animate();
    },
    syncView() {
      const y = 55 - this.depth;
      this.probe.position.y = y;
      
      // Update arrows based on pressure
      this.arrowGroup.clear();
      const p = this.rho * 9.8 * this.depth / 10000;
      for(let i=0; i<8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          const arrow = new THREE.ArrowHelper(
            new THREE.Vector3(Math.cos(angle), -1, Math.sin(angle)).normalize(),
            new THREE.Vector3(5 * Math.cos(angle), y + 1.5, 5 * Math.sin(angle)),
            p * 10, 0xff4757, 4, 1.5
          );
          this.arrowGroup.add(arrow);
      }
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);
      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.liquid-pressure-pro {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}
.viz-header {
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
  .formula-chip { color: #00d2ff; background: rgba(0, 210, 255, 0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; font-family: serif; }
}
.canvas-3d-wrapper {
  flex: 1.3; background: #000; border-radius: 12px; position: relative; overflow: hidden;
  .hud-top {
     position: absolute; top: 15px; left: 15px; display: flex; gap: 15px;
     .monitor {
        background: rgba(10, 10, 20, 0.7); backdrop-filter: blur(5px); border-left: 3px solid #00d2ff; padding: 5px 12px; border-radius: 6px;
        label { display: block; font-size: 0.55rem; color: #888; }
        span { font-size: 0.9rem; color: #fff; font-family: monospace; }
     }
  }
  .gauge-overlay {
    position: absolute; right: 20px; top: 50%; transform: translateY(-50%); text-align: center;
    .label { font-size: 0.6rem; color: #aaa; text-transform: uppercase; margin-top: 8px; }
  }
}
.bottom-controls { flex: 0.7; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; padding-bottom: 10px; }
.control-grid {
  background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 18px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  .item { label { font-size: 0.75rem; color: #aaa; margin-bottom: 8px; display: block; } }
}
.result-card {
  background: rgba(0, 210, 255, 0.05); border-radius: 12px; padding: 12px 18px; display: flex; gap: 15px; align-items: center;
  .icon-box { color: #00d2ff; font-size: 1.2rem; }
  p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; strong { color: #fff; } }
}
</style>
